import React from 'react';
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import type { CurveStyling, InterpolationMethod, LineStyle, PointStyle } from '@/interfaces/waveChart';

// Define the props interface for CurveFormatEditor
interface CurveFormatEditorProps {
  /** Current curve format/styling */
  format: CurveStyling;
  /** Callback when format changes */
  onFormatChange: (format: CurveStyling) => void;
  /** Whether the editor is disabled */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
}

const CurveFormatEditor: React.FC<CurveFormatEditorProps> = ({
  format,
  onFormatChange,
  disabled = false,
  className
}) => {
  // Debounced Display Name input state
  const [displayNameInput, setDisplayNameInput] = React.useState<string>(format.displayName ?? '')
  const debounceTimerRef = React.useRef<number | null>(null)

  // Keep local input in sync if parent format changes elsewhere
  React.useEffect(() => {
    setDisplayNameInput(format.displayName ?? '')
  }, [format.displayName])
  
  // Helper to create a new format object with updates
  const updateFormat = (updates: Partial<CurveStyling>) => {
    let newFormat = { ...format, ...updates };
    
    // Auto-enable data points when 'none' interpolation is selected
    if (updates.interpolation === 'none' && !newFormat.showDataPoints) {
      newFormat = { ...newFormat, showDataPoints: true };
    }
    
    console.log('🎨 CurveFormatEditor: Updating format:', { 
      old: format, 
      updates, 
      new: newFormat 
    });
    onFormatChange(newFormat);
  };

  // Debounce display name updates to avoid excessive redraws
  const handleDisplayNameChange = (value: string) => {
    setDisplayNameInput(value)
    if (debounceTimerRef.current) window.clearTimeout(debounceTimerRef.current)
    debounceTimerRef.current = window.setTimeout(() => {
      const trimmed = value.trim()
      updateFormat({ displayName: trimmed.length > 0 ? trimmed.slice(0, 100) : undefined })
    }, 300)
  }

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: 2, 
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider'
      }}
      className={className}
    >
      <Typography variant="h6" gutterBottom>
        Curve Formatting
      </Typography>

      {/* Display Name */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          size="small"
          label="Display Name"
          value={displayNameInput}
          onChange={(e) => handleDisplayNameChange(e.target.value)}
          disabled={disabled}
          inputProps={{ maxLength: 100 }}
          helperText="Shown in legends and tooltips. Does not rename the source curve."
        />
      </Box>

      {/* Line Styling Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Line Style
        </Typography>
        
        {/* Color Picker */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Color
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              component="input"
              type="color"
              value={format.color}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateFormat({ color: e.target.value })}
              disabled={disabled}
              sx={{
                width: 32,
                height: 32,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: disabled ? 'not-allowed' : 'pointer',
                '&::-webkit-color-swatch': {
                  borderRadius: '4px',
                  border: 'none'
                },
                '&::-moz-color-swatch': {
                  borderRadius: '4px',
                  border: 'none'
                }
              }}
            />
            <TextField
              value={format.color}
              onChange={(e) => updateFormat({ color: e.target.value })}
              disabled={disabled}
              size="small"
              sx={{ flex: 1 }}
              inputProps={{
                style: { fontFamily: 'monospace' }
              }}
            />
          </Box>
        </Box>

        {/* Line Thickness */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Line Thickness: {format.strokeThickness}px
          </Typography>
          <Slider
            value={format.strokeThickness}
            onChange={(_, value) => updateFormat({ strokeThickness: value as number })}
            min={0.1}
            max={3}
            step={0.1}
            disabled={disabled}
            marks={[
              { value: 0.1, label: '0.1' },
              { value: 1, label: '1' },
              { value: 2, label: '2' },
              { value: 3, label: '3' }
            ]}
          />
        </Box>

        {/* Line Style */}
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>Line Style</InputLabel>
            <Select
              value={format.lineStyle}
              onChange={(e) => updateFormat({ lineStyle: e.target.value as LineStyle })}
              label="Line Style"
            >
              <MenuItem value="solid">Solid</MenuItem>
              <MenuItem value="dashed">Dashed</MenuItem>
              <MenuItem value="dotted">Dotted</MenuItem>
              <MenuItem value="dashdot">Dash-Dot</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Opacity */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Opacity: {Math.round((format.opacity ?? 1) * 100)}%
          </Typography>
          <Slider
            value={format.opacity ?? 1}
            onChange={(_, value) => updateFormat({ opacity: value as number })}
            min={0.1}
            max={1.0}
            step={0.1}
            disabled={disabled}
            marks={[
              { value: 0.1, label: '10%' },
              { value: 0.5, label: '50%' },
              { value: 1.0, label: '100%' }
            ]}
          />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Interpolation Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Interpolation
        </Typography>
        
        <FormControl fullWidth size="small" disabled={disabled}>
          <InputLabel>Interpolation Method</InputLabel>
          <Select
            value={format.interpolation}
            onChange={(e) => updateFormat({ interpolation: e.target.value as InterpolationMethod })}
            label="Interpolation Method"
          >
            <MenuItem value="linear">Linear</MenuItem>
            <MenuItem value="stepBefore">Step (Before)</MenuItem>
            <MenuItem value="stepAfter">Step (After)</MenuItem>
            <MenuItem value="stepMiddle">Step (Middle)</MenuItem>
            <MenuItem value="spline">Spline</MenuItem>
            <MenuItem value="none">None (Points Only)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Data Points Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Data Points
        </Typography>
        
        {/* Show Data Points Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={format.showDataPoints}
              onChange={(e) => updateFormat({ showDataPoints: e.target.checked })}
              disabled={disabled}
            />
          }
          label="Show Data Points"
          sx={{ mb: 2 }}
        />

        {/* Point Style and Size - Only show if data points are enabled */}
        {format.showDataPoints && (
          <>
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth size="small" disabled={disabled}>
                <InputLabel>Point Style</InputLabel>
                <Select
                  value={format.pointStyle}
                  onChange={(e) => updateFormat({ pointStyle: e.target.value as PointStyle })}
                  label="Point Style"
                >
                  <MenuItem value="circle">Circle</MenuItem>
                  <MenuItem value="square">Square</MenuItem>
                  <MenuItem value="triangle">Triangle</MenuItem>
                  <MenuItem value="cross">Cross</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Point Size: {format.pointSize}px
              </Typography>
              <Slider
                value={format.pointSize}
                onChange={(_, value) => updateFormat({ pointSize: value as number })}
                min={2}
                max={12}
                step={1}
                disabled={disabled}
                marks={[
                  { value: 2, label: '2' },
                  { value: 6, label: '6' },
                  { value: 12, label: '12' }
                ]}
              />
            </Box>
          </>
        )}
      </Box>



    </Paper>
  );
};

export default CurveFormatEditor; 
